import { Route, Switch, useHistory } from "react-router-dom";
import { Card } from "@/src/components/Card";
import { TabConfig } from "@/src/components/nav/types";
import FirmNavbar from "@/src/components/nav/firm-navbar";
import { BuildingIcon, GitBranchIcon } from "lucide-react";
import { PipelineIndustries, PipelineIndustryDetail } from "./industries";
import {
  DealStatus,
  InternalPipelineDealsQuery,
  useInternalPipelineDealsQuery,
  useSellerDashboardQuery,
  useTteDealDataQuery,
} from "@/src/graphql/generated";
import useGqlClient from "@/src/hooks/useGqlClient";
import Loading from "@/src/components/Loading";
import CompanyLogo from "@/src/components/CompanyLogo";
import { formatDistanceToNowStrict, fromUnixTime } from "date-fns";
import {
  computeHighPriorityRatio,
  computeReadinessScore,
  computeWeightedDdqlRatio,
  fmtM,
  readinessLabel,
  Section,
} from "@/src/utils/dealReadiness";

const navLinks: TabConfig[] = [
  {
    label: "Deals",
    icon: <GitBranchIcon className="w-4 h-4 text-inherit" />,
    to: "/pipeline",
    exact: true,
  },
  {
    label: "Industries",
    icon: <BuildingIcon className="w-4 h-4 text-inherit" />,
    to: "/pipeline/industries",
  },
];

export function Pipeline() {
  return (
    <div>
      <div className="flex p-4 px-6 items-center justify-between">
        <p className="font-semibold text-lg">Pipeline</p>
      </div>
      <Card margin="s xl">
        <FirmNavbar tabs={navLinks} />
        <Switch>
          <Route path="/pipeline" exact>
            <PipelineDeals />
          </Route>
          <Route path="/pipeline/industries/:id">
            <PipelineIndustryDetail />
          </Route>
          <Route path="/pipeline/industries">
            <PipelineIndustries />
          </Route>
        </Switch>
      </Card>
    </div>
  );
}

function PipelineDeals() {
  const client = useGqlClient();
  const dealsQuery = useInternalPipelineDealsQuery(client);

  if (dealsQuery.error) {
    return (
      <div className="p-4">
        <p className="text-gray-700 font-semibold">Something went wrong</p>
      </div>
    );
  }

  if (dealsQuery.isLoading || !dealsQuery.data) {
    return (
      <div className="p-4">
        <Loading />
      </div>
    );
  }

  const deals = dealsQuery.data.internalPipelineDeals;

  if (deals.length === 0) {
    return (
      <div className="p-6 text-sm text-gray-500">No pipeline deals found.</div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr] gap-x-4 px-4 py-2 border-b border-gray-100 text-xs font-semibold text-gray-400 uppercase tracking-wide">
        <span>Company</span>
        <span>Industry</span>
        <span>Readiness</span>
        <span>Valuation</span>
        <span>Status</span>
      </div>
      <div className="divide-y divide-gray-100">
        {deals.map((deal) => (
          <DealRow key={deal.id} deal={deal} />
        ))}
      </div>
    </div>
  );
}

type BasicDeal = InternalPipelineDealsQuery["internalPipelineDeals"][number];

function DealRow({ deal }: { deal: BasicDeal }) {
  const history = useHistory();
  const client = useGqlClient();

  const tteQuery = useTteDealDataQuery(client, { dealId: deal.id });
  const dashboardQuery = useSellerDashboardQuery(client, { dealId: deal.id });

  const tte = tteQuery.data?.tteDealData;
  const valuation = tte?.latestValuation;
  const ebitda = valuation?.input.ebitda ?? tte?.ebitda ?? null;
  const mid = valuation?.output.mid ?? null;
  const industryName = tte?.industry?.industry ?? null;

  const sellerRequestList = dashboardQuery.data?.deal?.sellerRequestList;
  const sections: Section[] = sellerRequestList
    ? [...sellerRequestList.children].sort(
        (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
      )
    : [];

  const ddqlRatio = computeWeightedDdqlRatio(sections);
  const highPriorityRatio = computeHighPriorityRatio(sections);
  const readinessScore = computeReadinessScore({
    ddqlRatio,
    highPriorityRatio,
    valuation: valuation ?? null,
    ebitda,
    industry: tte?.industry ?? null,
  });

  const isLoading =
    (tteQuery.isLoading || dashboardQuery.isLoading) &&
    (!tteQuery.data || !dashboardQuery.data);

  return (
    <div
      className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr] gap-x-4 items-center px-4 py-3 hover:bg-gray-50 cursor-pointer"
      onClick={() => history.push(`/deal/${deal.id}`)}
    >
      {/* Company */}
      <div className="flex items-center gap-x-3 min-w-0">
        <CompanyLogo
          logo={deal.company.logo}
          bgColor={deal.company.logoColor}
          name={deal.company.name}
          size={32}
        />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">
            {deal.company.name}
          </p>
          <p className="text-xs text-gray-400">
            {formatDistanceToNowStrict(fromUnixTime(deal.createdAt), {
              addSuffix: true,
            })}
          </p>
        </div>
      </div>

      {/* Industry */}
      <div className="text-sm text-gray-600 truncate">
        {isLoading ? (
          <span className="inline-block w-20 h-3 bg-gray-100 rounded animate-pulse" />
        ) : (
          industryName ?? <span className="text-gray-300">—</span>
        )}
      </div>

      {/* Readiness */}
      <div>
        {isLoading ? (
          <span className="inline-block w-16 h-3 bg-gray-100 rounded animate-pulse" />
        ) : sections.length > 0 || valuation ? (
          <ReadinessBadge score={readinessScore} />
        ) : (
          <span className="text-gray-300 text-sm">—</span>
        )}
      </div>

      {/* Valuation */}
      <div className="text-sm text-gray-700 font-medium">
        {isLoading ? (
          <span className="inline-block w-16 h-3 bg-gray-100 rounded animate-pulse" />
        ) : mid ? (
          fmtM(mid)
        ) : (
          <span className="text-gray-300">—</span>
        )}
      </div>

      {/* Status */}
      <div>
        <DealStatusBadge status={deal.status} />
      </div>
    </div>
  );
}

function ReadinessBadge({ score }: { score: number }) {
  const label = readinessLabel(score);

  const colorClass =
    score >= 76
      ? "bg-green-100 text-green-800"
      : score >= 40
        ? "bg-yellow-100 text-yellow-800"
        : "bg-red-100 text-red-700";

  return (
    <span
      className={`inline-flex items-center gap-x-1 rounded-full px-2 py-0.5 text-xs font-medium ${colorClass}`}
    >
      <span className="font-bold">{score}</span>
      <span>{label}</span>
    </span>
  );
}

function DealStatusBadge({ status }: { status: DealStatus }) {
  const labels: Record<DealStatus, string> = {
    [DealStatus.Active]: "Active",
    [DealStatus.Archived]: "Archived",
  };

  const colors: Record<DealStatus, string> = {
    [DealStatus.Active]: "bg-green-100 text-green-800",
    [DealStatus.Archived]: "bg-gray-100 text-gray-600",
  };

  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${colors[status]}`}
    >
      {labels[status]}
    </span>
  );
}

